// src/utils/sessionManager.ts
// Session management for web — access token in memory only;
// refresh token lives in HttpOnly cookie (set by backend).
// CSRF token from login/refresh JSON is kept in memory + localStorage so a
// browser restart or new tab can still send X-CSRF-Token. Unlike access and
// refresh tokens, this double-submit value is intentionally JS-readable.

interface MemorySession {
  token: string | null;
  expiresAt: number | null;
  csrfToken: string | null;
}

/** SPA-origin storage only — never put refresh/access tokens here. */
export const CSRF_STORAGE_KEY = 'lb_csrf_token';
// Backwards-compatible export for callers compiled against the old name.
export const CSRF_SESSION_STORAGE_KEY = CSRF_STORAGE_KEY;

let memorySession: MemorySession = {
  token: null,
  expiresAt: null,
  csrfToken: null,
};

const LEGACY_KEYS = [
  'token',
  'refreshToken',
  'auth_token',
  'refresh_token',
  'token_expiry',
];

const readStoredCsrf = (): string | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const persisted = localStorage.getItem(CSRF_STORAGE_KEY);
    if (persisted) return persisted;

    // One-time migration for sessions created before CSRF became cross-tab.
    const legacy = typeof sessionStorage === 'undefined'
      ? null
      : sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (legacy) {
      localStorage.setItem(CSRF_STORAGE_KEY, legacy);
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
    }
    return legacy;
  } catch {
    return null;
  }
};

const writeStoredCsrf = (token: string | null): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(CSRF_STORAGE_KEY, token);
    else localStorage.removeItem(CSRF_STORAGE_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
};

/** Hydrate in-memory CSRF from persistent storage (reload / new tab / restart). */
export const hydrateCsrfTokenFromStorage = (): string | null => {
  const stored = readStoredCsrf();
  memorySession.csrfToken = stored;
  return memorySession.csrfToken;
};

// Cold-start hydrate when the module loads in the browser
hydrateCsrfTokenFromStorage();

/**
 * Wipe legacy localStorage token material (XSS-accessible).
 */
export const clearLegacyLocalStorageTokens = (): void => {
  try {
    for (const key of LEGACY_KEYS) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
};

/**
 * Store access token in memory only. Refresh stays in HttpOnly cookie from login/refresh.
 */
export const storeTokens = (token: string, _refreshToken?: string, expiresIn?: number): void => {
  memorySession.token = token;
  memorySession.expiresAt = expiresIn
    ? Date.now() + expiresIn * 1000
    : Date.now() + 3600 * 1000;
  clearLegacyLocalStorageTokens();
  console.log('[SessionManager] Access token stored in memory');
};

/**
 * Persist CSRF token from login/refresh JSON for cross-origin double-submit.
 * Survives hard reload, new tabs, and browser restarts on the SPA origin.
 */
export const storeCsrfToken = (csrfToken?: string | null): void => {
  if (csrfToken) {
    memorySession.csrfToken = csrfToken;
    writeStoredCsrf(csrfToken);
  }
};

export const getToken = (): string | null => memorySession.token;

/**
 * Web refresh uses HttpOnly cookie — no JS-readable refresh token.
 */
export const getRefreshToken = (): string | null => null;

export const getCsrfToken = (): string | null => {
  // Re-read persistent storage so a refresh rotation in another tab updates
  // this tab before its next cookie-authenticated request.
  return hydrateCsrfTokenFromStorage();
};

export const getTokenExpiry = (): number | null => memorySession.expiresAt;

export const isTokenExpired = (): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return !getToken();
  const bufferMs = 60 * 1000;
  return Date.now() >= expiry - bufferMs;
};

export const isTokenExpiringSoon = (): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return false;
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() >= expiry - fiveMinutes;
};

export const clearTokens = (options: { preserveCsrf?: boolean } = {}): void => {
  const csrfToken = options.preserveCsrf ? getCsrfToken() : null;
  memorySession = { token: null, expiresAt: null, csrfToken };
  if (!options.preserveCsrf) {
    writeStoredCsrf(null);
  }
  clearLegacyLocalStorageTokens();
  console.log('[SessionManager] Memory session cleared');
};

export const getTimeUntilExpiry = (): number | null => {
  const expiry = getTokenExpiry();
  if (!expiry) return null;
  const timeLeft = expiry - Date.now();
  return timeLeft > 0 ? timeLeft : 0;
};

export const formatTimeRemaining = (): string | null => {
  const timeLeft = getTimeUntilExpiry();
  if (timeLeft === null) return null;
  const minutes = Math.floor(timeLeft / (60 * 1000));
  const seconds = Math.floor((timeLeft % (60 * 1000)) / 1000);
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const isSessionValid = (): boolean => {
  const token = getToken();
  return token !== null && !isTokenExpired();
};

const sessionManager = {
  storeTokens,
  storeCsrfToken,
  hydrateCsrfTokenFromStorage,
  getToken,
  getRefreshToken,
  getCsrfToken,
  getTokenExpiry,
  isTokenExpired,
  isTokenExpiringSoon,
  clearTokens,
  clearLegacyLocalStorageTokens,
  getTimeUntilExpiry,
  formatTimeRemaining,
  isSessionValid,
  CSRF_STORAGE_KEY,
  CSRF_SESSION_STORAGE_KEY,
};

export default sessionManager;
